import { defineStore } from 'pinia'
import { useWindowSize } from '@vueuse/core'

import { screens } from '@/theme.config'
import { checkWebview } from '@/utils/common'

import type { GeolocationState } from '@/types/commons'

export const useAppStore = defineStore('app', () => {
  const { coords, locatedAt, resume } = useGeolocation()

  resume()

  const isFullPageLoadingShown = ref(false)
  const isLangSwitcherShown = ref(false)

  const device = useDevice()
  const { width, height } = useWindowSize({ initialWidth: device.isMobile ? screens.sm : screens.xl, initialHeight: 0 })
  const isWebview = computed(() => checkWebview(device.userAgent))

  const latitude = computed(() => coords.value?.latitude)
  const longitude = computed(() => coords.value?.longitude)
  const isLocationAcquired = computed(() => !!locatedAt.value)
  const geolocation = computed<GeolocationState>(() => (isLocationAcquired.value ? { lat: latitude.value, lng: longitude.value } : {}))

  const isMobile = computed(() => width.value < screens.sm)
  const isTablet = computed(() => width.value >= screens.sm && width.value < screens.md)
  const isDesktop = computed(() => width.value >= screens.md && width.value < screens.lg)
  const isLargeDesktop = computed(() => width.value >= screens.lg && width.value < screens.xl)
  const isExtraLargeDesktop = computed(() => width.value >= screens.xl)

  const isLargeDesktopScreen = computed(() => isLargeDesktop.value || isExtraLargeDesktop.value)
  const isDesktopScreen = computed(() => isDesktop.value || isLargeDesktopScreen.value)
  const isMobileScreen = computed(() => isMobile.value || isTablet.value)

  return {
    windowWidth: width,
    windowHeight: height,
    isFullPageLoadingShown,
    isLangSwitcherShown,
    geolocation,
    isLocationAcquired,
    isWebview,
    /** window width < 600 */
    isMobile,
    /** window width >= 600 && < 768 */
    isTablet,
    /** window width >= 768 && < 960 */
    isDesktop,
    /** window width >= 960 && < 1280 */
    isLargeDesktop,
    /** window width >= 1280 */
    isExtraLargeDesktop,
    /** window width >= 768 */
    isDesktopScreen,
    /** window width >= 960 */
    isLargeDesktopScreen,
    /** window width < 768 */
    isMobileScreen,
  }
})
